import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Heart, MessageCircle, Gift, Share2, X, Music, Send, MoreVertical, Trash2 } from "lucide-react";
import GiftSheet from "./GiftSheet";
import CommentsSheet from "./CommentsSheet";
import ShareSheet from "./ShareSheet";
import SendReelSheet from "./SendReelSheet";

/**
 * Full-feature TikTok-style scrollable player for a SINGLE user's reels.
 * Opens when a profile reel thumbnail is tapped, starts at startIndex, allows vertical scroll
 * through all the user's reels with like / comment / gift / share buttons (like the main feed).
 */
export default function ProfileReelPlayer({ reels, startIndex = 0, onClose }) {
  const { user: me } = useAuth();
  const containerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(startIndex);
  const [muted, setMuted] = useState(false);
  const [localReels, setLocalReels] = useState(reels);
  const [giftReel, setGiftReel] = useState(null);
  const [commentReel, setCommentReel] = useState(null);
  const [shareReel, setShareReel] = useState(null);
  const [sendReel, setSendReel] = useState(null);
  const [moreReel, setMoreReel] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const onDelete = async (reel) => {
    if (!window.confirm("Delete this reel? This action is permanent.")) return;
    setBusyId(reel.id);
    try {
      await api.delete(`/reels/${reel.id}`);
      setLocalReels(rs => rs.filter(r => r.id !== reel.id));
      setMoreReel(null);
      // If we just deleted the last reel, close the player
      if (localReels.length <= 1) onClose?.();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not delete reel");
    } finally { setBusyId(null); }
  };

  // Scroll to startIndex after mount
  useEffect(() => {
    const t = setTimeout(() => {
      const card = containerRef.current?.children[startIndex];
      card?.scrollIntoView({ behavior: "instant", block: "start" });
    }, 50);
    return () => clearTimeout(t);
  }, [startIndex]);

  useEffect(() => {
    const c = containerRef.current; if (!c) return;
    const onScroll = () => {
      const idx = Math.round(c.scrollTop / c.clientHeight);
      if (idx !== activeIdx) setActiveIdx(idx);
    };
    c.addEventListener("scroll", onScroll, { passive: true });
    return () => c.removeEventListener("scroll", onScroll);
  }, [activeIdx]);

  const onLike = async (reelId) => {
    try {
      const { data } = await api.post(`/reels/${reelId}/like`);
      setLocalReels(rs => rs.map(r => r.id === reelId ? { ...r, likes: data.likes, liked_by_me: data.liked } : r));
    } catch { /* noop */ }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black" data-testid="profile-reel-player">
      <button onClick={onClose} data-testid="profile-reel-player-close"
        className="absolute top-4 right-4 z-30 w-10 h-10 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white active:scale-90">
        <X className="w-5 h-5"/>
      </button>

      <div ref={containerRef} className="h-[100dvh] overflow-y-scroll snap-y snap-mandatory scrollbar-hide">
        {localReels.map((reel, i) => (
          <ReelSlide key={reel.id} reel={reel}
            isActive={i === activeIdx}
            muted={muted}
            isOwn={me?.id && (reel.user_id === me.id || reel.user?.id === me.id)}
            onMute={() => setMuted(m => !m)}
            onLike={() => onLike(reel.id)}
            onComment={() => setCommentReel(reel)}
            onGift={() => setGiftReel(reel)}
            onShare={() => setShareReel(reel)}
            onSendInApp={() => setSendReel(reel)}
            onMore={() => setMoreReel(reel)}/>
        ))}
      </div>

      {giftReel && <GiftSheet reel={giftReel} onClose={() => setGiftReel(null)}/>}
      {commentReel && <CommentsSheet reel={commentReel} onClose={() => setCommentReel(null)}/>}
      {shareReel && <ShareSheet reel={shareReel} onClose={() => setShareReel(null)}
        url={`${window.location.origin}/?reel=${shareReel.id}`}
        title={shareReel.title || "Hifireels Reel"}
        onSendInApp={() => { const r = shareReel; setShareReel(null); setSendReel(r); }}/>}
      {sendReel && <SendReelSheet reel={sendReel} onClose={() => setSendReel(null)}/>}

      {moreReel && (
        <div className="fixed inset-0 z-[90] bg-black/70 flex items-end" onClick={() => setMoreReel(null)} data-testid="prp-more-sheet">
          <div className="w-full max-w-md mx-auto glass rounded-t-3xl p-5 space-y-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-outfit font-bold text-lg">Reel options</h3>
              <button onClick={() => setMoreReel(null)} data-testid="prp-more-close" className="p-1 rounded-full hover:bg-white/10"><X className="w-5 h-5"/></button>
            </div>
            <button onClick={() => onDelete(moreReel)} disabled={busyId === moreReel.id} data-testid="prp-delete"
              className="w-full flex items-center gap-3 p-3 rounded-2xl bg-[#FF2A5F]/10 border border-[#FF2A5F]/30 active:scale-[0.98] disabled:opacity-50">
              <Trash2 className="w-5 h-5 text-[#FF2A5F]"/>
              <div className="text-left">
                <div className="font-bold text-sm text-[#FF2A5F]">Delete reel</div>
                <div className="text-[11px] text-white/55">This cannot be undone</div>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReelSlide({ reel, isActive, muted, isOwn, onMute, onLike, onComment, onGift, onShare, onSendInApp, onMore }) {
  const vRef = useRef(null);
  const [liked, setLiked] = useState(reel.liked_by_me || false);

  useEffect(() => {
    const v = vRef.current; if (!v) return;
    if (isActive) { v.play().catch(() => {}); api.post(`/reels/${reel.id}/view`).catch(() => {}); }
    else { v.pause(); v.currentTime = 0; }
  }, [isActive, reel.id]);

  const tapLike = async () => {
    setLiked(l => !l);
    onLike();
  };

  const src = reel.video_url && reel.video_url.includes("res.cloudinary.com") && reel.video_url.includes("/video/upload/")
    ? reel.video_url.replace("/video/upload/", "/video/upload/q_auto,f_auto,w_720/")
    : reel.video_url;

  return (
    <div className="relative h-[100dvh] w-full snap-start flex items-center justify-center bg-black overflow-hidden">
      <video ref={vRef} src={src} poster={reel.thumbnail_url || undefined}
        className="absolute inset-0 w-full h-full object-cover"
        loop muted={muted} playsInline preload={isActive ? "auto" : "metadata"}
        onClick={onMute}
        data-testid={`prp-video-${reel.id}`}/>
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/85 pointer-events-none"/>

      {/* Right rail */}
      <div className="absolute right-3 bottom-28 flex flex-col items-center gap-5 z-10">
        <button onClick={tapLike} className="flex flex-col items-center" data-testid={`prp-like-${reel.id}`}>
          <Heart className={`w-8 h-8 ${liked ? "fill-[#FF2A5F] text-[#FF2A5F]" : "text-white"}`}/>
          <span className="text-xs font-semibold mt-1">{reel.likes}</span>
        </button>
        <button onClick={onComment} className="flex flex-col items-center" data-testid={`prp-comment-${reel.id}`}>
          <MessageCircle className="w-8 h-8"/>
          <span className="text-xs mt-1">{reel.comments_count}</span>
        </button>
        <button onClick={onGift} className="flex flex-col items-center" data-testid={`prp-gift-${reel.id}`}>
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF7A00] to-[#FF2A5F] flex items-center justify-center neon-gold">
            <Gift className="w-5 h-5 text-black"/>
          </div>
          <span className="text-xs mt-1 text-[#FF7A00] font-bold">Gift</span>
        </button>
        <button onClick={onShare} className="flex flex-col items-center" data-testid={`prp-share-${reel.id}`}>
          <Share2 className="w-8 h-8"/>
          <span className="text-xs mt-1">{reel.share_count || 0}</span>
        </button>
        <button onClick={onSendInApp} className="flex flex-col items-center" data-testid={`prp-send-${reel.id}`}>
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#A855F7] flex items-center justify-center">
            <Send className="w-5 h-5 text-black"/>
          </div>
          <span className="text-xs mt-1 text-[#00E5FF] font-bold">Send</span>
        </button>
        {isOwn && (
          <button onClick={onMore} className="flex flex-col items-center" data-testid={`prp-more-${reel.id}`} aria-label="More options">
            <MoreVertical className="w-8 h-8 text-white"/>
          </button>
        )}
      </div>

      {/* Caption */}
      <div className="absolute bottom-12 left-4 right-20 z-10">
        <div className="font-outfit font-bold text-lg leading-tight">{reel.user?.name || `@${reel.user?.username}`}</div>
        {reel.user?.name && <div className="text-[11px] text-white/55 -mt-0.5">@{reel.user?.username}</div>}
        {reel.title && <p className="text-sm text-white/90 mt-1 line-clamp-2">{reel.title}</p>}
        {reel.description && <p className="text-xs text-white/80 mt-1 line-clamp-2">{reel.description}</p>}
        <div className="flex items-center gap-2 mt-2 text-xs">
          <Music className="w-3 h-3"/><span className="text-white/80">Original audio</span>
        </div>
      </div>
    </div>
  );
}
